import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowRight, Eye, EyeOff, Lock, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { toast } from 'sonner'
import { useChangePassword } from '@/utils/api/user-service'
import { useNavigate } from '@tanstack/react-router'
import { useAuth } from './auth-provider'

const schema = z.object({
  currentPassword: z.string().min(1, 'Enter your current password'),
  newPassword: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Must contain at least one number'),
  confirmPassword: z.string(),
}).refine((d) => d.newPassword === d.confirmPassword, {
  path: ['confirmPassword'],
  message: "Passwords don't match",
})

type FormValues = z.infer<typeof schema>
const POST_FORCE_RESET_REDIRECT_KEY = 'post_force_reset_redirect'

export function ForcePasswordResetForm() {
  const navigate = useNavigate()
  const { logout } = useAuth()
  const [showPassword, setShowPassword] = useState({ current: false, next: false, confirm: false })
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  })

  const mutation = useChangePassword()
  const watchNewPassword = form.watch('newPassword')

  const passwordChecklist = useMemo(() => ([
    { label: 'At least 8 characters', passed: watchNewPassword.length >= 8 },
    { label: 'One uppercase letter', passed: /[A-Z]/.test(watchNewPassword) },
    { label: 'One lowercase letter', passed: /[a-z]/.test(watchNewPassword) },
    { label: 'One number', passed: /[0-9]/.test(watchNewPassword) },
  ]), [watchNewPassword])

  const handleSubmit = async (values: FormValues) => {
    try {
      await mutation.mutateAsync({ data: { currentPassword: values.currentPassword, newPassword: values.newPassword } })
      toast.success('Password updated successfully. You will be redirected to sign in with your new password.')
      try { window.localStorage.removeItem('force_password_reset') } catch {}

      let postResetRedirect = '/dashboard'
      try {
        const savedRedirect = window.sessionStorage.getItem(POST_FORCE_RESET_REDIRECT_KEY)
        if (savedRedirect?.startsWith('/')) {
          postResetRedirect = savedRedirect
        }
      }
      catch {}

      logout()
      // Give the user a brief moment to read the message before redirecting
      setTimeout(() => navigate({
        to: '/login',
        search: { redirect: postResetRedirect },
        replace: true,
        reloadDocument: true,
      }), 1600)
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Unable to change password. Please verify your current password.'
      toast.error(message)
    }
  }

  return (
    <Card className="w-full max-w-xl mx-auto border-0 bg-white/80 text-slate-900 backdrop-blur-sm shadow-2xl dark:bg-slate-900/80 dark:text-slate-100">
      <CardHeader className="space-y-2">
        <CardTitle className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Update your password</CardTitle>
        <CardDescription className="text-slate-600 dark:text-slate-300">
          For security reasons you must change your password before continuing.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Form {...form}>
          <form className="space-y-4" onSubmit={form.handleSubmit(handleSubmit)}>
            <FormField
              name="currentPassword"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel required className="text-slate-700 dark:text-slate-200">Current password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-slate-500 dark:text-slate-300" />
                      <Input
                        type={showPassword.current ? 'text' : 'password'}
                        className="h-11 pl-10 pr-12 text-slate-900 placeholder:text-slate-500 dark:text-slate-100 dark:placeholder:text-slate-400"
                        autoComplete="current-password"
                        placeholder="Enter current password"
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1 h-9 w-9 text-slate-500 dark:text-slate-300"
                        onClick={() => setShowPassword((s) => ({ ...s, current: !s.current }))}
                        aria-label={showPassword.current ? 'Hide password' : 'Show password'}
                      >
                        {showPassword.current ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="newPassword"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel required className="text-slate-700 dark:text-slate-200">New password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <RefreshCw className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-slate-500 dark:text-slate-300" />
                      <Input
                        type={showPassword.next ? 'text' : 'password'}
                        className="h-11 pl-10 pr-12 text-slate-900 placeholder:text-slate-500 dark:text-slate-100 dark:placeholder:text-slate-400"
                        autoComplete="new-password"
                        placeholder="Create a strong password"
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1 h-9 w-9 text-slate-500 dark:text-slate-300"
                        onClick={() => setShowPassword((s) => ({ ...s, next: !s.next }))}
                        aria-label={showPassword.next ? 'Hide password' : 'Show password'}
                      >
                        {showPassword.next ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                      </Button>
                    </div>
                  </FormControl>
                  <div className="mt-2 space-y-1">
                    {passwordChecklist.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs">
                        <div className={`h-3 w-3 rounded-full ${item.passed ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-slate-600'}`} />
                        <span className={item.passed ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-600 dark:text-slate-300'}>
                          {item.label}
                        </span>
                      </div>
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="confirmPassword"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel required className="text-slate-700 dark:text-slate-200">Confirm new password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-slate-500 dark:text-slate-300" />
                      <Input
                        type={showPassword.confirm ? 'text' : 'password'}
                        className="h-11 pl-10 pr-12 text-slate-900 placeholder:text-slate-500 dark:text-slate-100 dark:placeholder:text-slate-400"
                        autoComplete="new-password"
                        placeholder="Re-enter new password"
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1 h-9 w-9 text-slate-500 dark:text-slate-300"
                        onClick={() => setShowPassword((s) => ({ ...s, confirm: !s.confirm }))}
                        aria-label={showPassword.confirm ? 'Hide password' : 'Show password'}
                      >
                        {showPassword.confirm ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button className="w-full h-11" disabled={mutation.isPending}>
              {mutation.isPending ? 'Updating…' : (
                <span className="inline-flex items-center gap-2">
                  Update password
                  <ArrowRight className="size-4" />
                </span>
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
